import React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { useSettings } from "../contexts/SettingsContext";
import CityCombobox from "../components/CityCombobox";

export default function Checkout() {
    const navigate = useNavigate();
    const { cart, refresh, isGuest } = useCart();
    const { user } = useAuth();
    const { settings } = useSettings();
    const [loading, setLoading] = React.useState(false);
    const [code, setCode] = React.useState("");
    const [codeInfo, setCodeInfo] = React.useState(null);
    const [creditBalance, setCreditBalance] = React.useState(0);
    const [useCredits, setUseCredits] = React.useState(false);
    const [loyaltyStatus, setLoyaltyStatus] = React.useState(null);
    const [useLoyaltyPoints, setUseLoyaltyPoints] = React.useState(false);
    const [sameShipping, setSameShipping] = React.useState(true);
    const [wantsInstall, setWantsInstall] = React.useState(false);
    const [deliveryMethod, setDeliveryMethod] = React.useState("fan_courier");
    const stripeEnabled = settings?.stripe_enabled !== false && settings?.stripe_enabled !== "false";
    const codEnabled = settings?.cod_enabled !== false && settings?.cod_enabled !== "false";
    const availablePayments = [
        stripeEnabled && { k: "stripe", l: "Card bancar (Stripe)", d: "Plată securizată online" },
        codEnabled   && { k: "cod",    l: "Ramburs la livrare",   d: "Plătești la primire" },
    ].filter(Boolean);
    const [paymentMethod, setPaymentMethod] = React.useState("stripe");
    // Sync default payment method once settings load
    React.useEffect(() => {
        if (!stripeEnabled && codEnabled) setPaymentMethod("cod");
        else if (stripeEnabled) setPaymentMethod("stripe");
    }, [stripeEnabled, codEnabled]);
    const [billing, setBilling] = React.useState({ name: user?.name || "", phone: user?.phone || "", email: user?.email || "", county: "", city: "", postal_code: "", street: "", number: "", bloc_ap: "", notes: "" });
    const [shipping, setShipping] = React.useState({ name: "", phone: "", email: "", county: "", city: "", postal_code: "", street: "", number: "", bloc_ap: "", notes: "" });
    const [counties, setCounties] = React.useState([]);
    const [billingCities, setBillingCities] = React.useState([]);
    const [shippingCities, setShippingCities] = React.useState([]);

    React.useEffect(() => { refresh(); }, [refresh]);
    React.useEffect(() => {
        if (!isGuest) {
            api.get("/credits/balance").then(r => setCreditBalance(r.data?.balance || 0)).catch(() => {});
            api.get("/loyalty/status").then(r => setLoyaltyStatus(r.data)).catch(() => {});
        }
    }, [isGuest]);
    React.useEffect(() => { api.get("/locations/counties").then(r => setCounties(r.data || [])).catch(() => {}); }, []);
    React.useEffect(() => {
        if (billing.county) api.get("/locations/cities", { params: { county: billing.county } }).then(r => setBillingCities(r.data || []));
        else setBillingCities([]);
    }, [billing.county]);
    React.useEffect(() => {
        if (shipping.county) api.get("/locations/cities", { params: { county: shipping.county } }).then(r => setShippingCities(r.data || []));
        else setShippingCities([]);
    }, [shipping.county]);

    const applyCode = async () => {
        try {
            const res = await api.post("/discounts/validate", { code });
            setCodeInfo(res.data);
            toast.success("Cod aplicat!");
        } catch {
            setCodeInfo(null);
            toast.error("Cod invalid");
        }
    };

    const shippingCost = deliveryMethod === "pickup" ? 0 : 25;
    const subtotal = cart.subtotal || 0;
    const userDisc = cart.user_discount_amount || 0;
    const codeDisc = codeInfo ? (codeInfo.discount_type === "percent" ? Math.round(subtotal * codeInfo.value) / 100 : codeInfo.value) : 0;
    const beforeCredits = Math.max(0, subtotal - userDisc - codeDisc + shippingCost);
    const creditDisc = useCredits ? Math.min(creditBalance, beforeCredits) : 0;
    const loyaltyRedeemValue = loyaltyStatus ? (loyaltyStatus.points * 0.1) : 0;
    const loyaltyDisc = useLoyaltyPoints ? Math.min(loyaltyRedeemValue, Math.max(0, beforeCredits - creditDisc)) : 0;
    const loyaltyPointsToRedeem = useLoyaltyPoints ? Math.ceil(loyaltyDisc / 0.1) : 0;
    const finalTotal = Math.max(0, beforeCredits - creditDisc - loyaltyDisc);

    const buildAddress = (d) => {
        const { street, number, bloc_ap, ...rest } = d;
        const address = [street, number, bloc_ap].filter(Boolean).join(", ");
        return { ...rest, address };
    };

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = {
                origin: window.location.origin,
                billing: buildAddress(billing),
                shipping: sameShipping ? buildAddress(billing) : buildAddress(shipping),
                delivery_method: deliveryMethod,
                payment_method: paymentMethod,
                discount_code: codeInfo?.code,
                wants_installation: wantsInstall,
                use_credits: useCredits && creditDisc > 0,
                loyalty_points_redeemed: loyaltyPointsToRedeem > 0 ? loyaltyPointsToRedeem : undefined,
            };
            let res;
            if (isGuest) {
                payload.items = cart.items;
                res = await api.post("/payments/checkout-guest", payload);
            } else {
                res = await api.post("/payments/checkout", payload);
            }
            if (res.data.method === "cod" || res.data.redirect) {
                toast.success("Comandă plasată!");
                navigate(res.data.redirect ? new URL(res.data.redirect).pathname + new URL(res.data.redirect).search : `/checkout/success?order_id=${res.data.order_id}`);
            } else if (res.data.url) {
                window.location.href = res.data.url;
            }
        } catch (e) {
            toast.error("Eroare la finalizarea comenzii");
            setLoading(false);
        }
    };

    const fmt = (n) => `${(n || 0).toFixed(2)} RON`;

    if (!cart.items || cart.items.length === 0) {
        return <div className="container-x py-20 text-center"><h1 className="font-heading text-3xl mb-4">Coșul este gol</h1></div>;
    }

    return (
        <form onSubmit={submit} className="container-x py-10" data-testid="checkout-page">
            <h1 className="font-heading text-4xl mb-2">Finalizare comandă</h1>
            {isGuest && <p className="text-sm text-muted-foreground mb-6">Comandă ca vizitator. Pentru a folosi beneficiile de client (discount-uri, istoric), <button type="button" onClick={() => navigate("/login?next=/checkout")} className="text-primary underline">autentifică-te</button>.</p>}
            {!isGuest && <p className="text-sm text-muted-foreground mb-6">Autentificat ca {user.email}</p>}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-6">
                    <div className="card-soft p-6">
                        <h2 className="font-heading text-xl mb-4">Date facturare</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <input required placeholder="Nume complet" value={billing.name} onChange={(e) => setBilling(b => ({ ...b, name: e.target.value }))} className="input-soft" data-testid="ck-billing-name" />
                            <input required placeholder="Telefon" value={billing.phone} onChange={(e) => setBilling(b => ({ ...b, phone: e.target.value }))} className="input-soft" />
                            <input required type="email" placeholder="Email" value={billing.email} onChange={(e) => setBilling(b => ({ ...b, email: e.target.value }))} className="input-soft md:col-span-2" />
                            <select value={billing.county} onChange={(e) => setBilling(b => ({ ...b, county: e.target.value, city: "" }))} className="input-soft">
                                <option value="">Județ</option>
                                {counties.map(c => <option key={c}>{c}</option>)}
                            </select>
                            <CityCombobox
                                cities={billingCities}
                                value={billing.city}
                                onChange={(v) => setBilling(b => ({ ...b, city: v }))}
                                onPostalCode={(pc) => setBilling(b => ({ ...b, postal_code: b.postal_code || pc }))}
                                placeholder="Localitate (oraș / sat / comună)"
                                required
                            />
                            <input placeholder="Cod poștal" value={billing.postal_code} onChange={(e) => setBilling(b => ({ ...b, postal_code: e.target.value }))} className="input-soft" />
                            <div className="md:col-span-2 grid grid-cols-3 gap-3">
                                <input placeholder="Strada" value={billing.street} onChange={(e) => setBilling(b => ({ ...b, street: e.target.value }))} className="input-soft col-span-2" />
                                <input placeholder="Număr" value={billing.number} onChange={(e) => setBilling(b => ({ ...b, number: e.target.value }))} className="input-soft" />
                            </div>
                            <input placeholder="Bloc / Scară / Apartament (opțional)" value={billing.bloc_ap} onChange={(e) => setBilling(b => ({ ...b, bloc_ap: e.target.value }))} className="input-soft md:col-span-2" />
                        </div>
                        <label className="flex items-center gap-2 mt-4 text-sm">
                            <input type="checkbox" checked={sameShipping} onChange={(e) => setSameShipping(e.target.checked)} />
                            <span>Livrare la aceeași adresă</span>
                        </label>
                    </div>

                    {!sameShipping && (
                        <div className="card-soft p-6">
                            <h2 className="font-heading text-xl mb-4">Adresă livrare</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <input required placeholder="Nume" value={shipping.name} onChange={(e) => setShipping(s => ({ ...s, name: e.target.value }))} className="input-soft" />
                                <input required placeholder="Telefon" value={shipping.phone} onChange={(e) => setShipping(s => ({ ...s, phone: e.target.value }))} className="input-soft" />
                                <select value={shipping.county} onChange={(e) => setShipping(s => ({ ...s, county: e.target.value, city: "" }))} className="input-soft">
                                    <option value="">Județ</option>
                                    {counties.map(c => <option key={c}>{c}</option>)}
                                </select>
                                <CityCombobox
                                    cities={shippingCities}
                                    value={shipping.city}
                                    onChange={(v) => setShipping(s => ({ ...s, city: v }))}
                                    onPostalCode={(pc) => setShipping(s => ({ ...s, postal_code: s.postal_code || pc }))}
                                    placeholder="Localitate"
                                    required
                                />
                                <input placeholder="Cod poștal" value={shipping.postal_code} onChange={(e) => setShipping(s => ({ ...s, postal_code: e.target.value }))} className="input-soft" />
                                <div className="md:col-span-2 grid grid-cols-3 gap-3">
                                    <input placeholder="Strada" value={shipping.street} onChange={(e) => setShipping(s => ({ ...s, street: e.target.value }))} className="input-soft col-span-2" />
                                    <input placeholder="Număr" value={shipping.number} onChange={(e) => setShipping(s => ({ ...s, number: e.target.value }))} className="input-soft" />
                                </div>
                                <input placeholder="Bloc / Scară / Apartament (opțional)" value={shipping.bloc_ap} onChange={(e) => setShipping(s => ({ ...s, bloc_ap: e.target.value }))} className="input-soft md:col-span-2" />
                            </div>
                        </div>
                    )}

                    <div className="card-soft p-6">
                        <h2 className="font-heading text-xl mb-4">Metoda de livrare</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            {[{ k: "fan_courier", l: "Fan Courier", p: "25.00 RON" }, { k: "sameday", l: "Sameday", p: "25.00 RON" }, { k: "pickup", l: "Ridicare personală", p: "Gratuit" }].map(m => (
                                <label key={m.k} className={`border-2 rounded-xl p-4 cursor-pointer transition-colors ${deliveryMethod === m.k ? "border-primary bg-primary/5" : "border-border hover:border-foreground/50"}`} data-testid={`delivery-${m.k}`}>
                                    <input type="radio" name="delivery" checked={deliveryMethod === m.k} onChange={() => setDeliveryMethod(m.k)} className="mr-2" />
                                    <span className="font-medium">{m.l}</span>
                                    <div className="text-xs text-muted-foreground mt-1">{m.p}</div>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="card-soft p-6">
                        <h2 className="font-heading text-xl mb-4">Metoda de plată</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {availablePayments.map(m => (
                                <label key={m.k} className={`border-2 rounded-xl p-4 cursor-pointer transition-colors ${paymentMethod === m.k ? "border-primary bg-primary/5" : "border-border hover:border-foreground/50"}`} data-testid={`payment-${m.k}`}>
                                    <input type="radio" name="payment" checked={paymentMethod === m.k} onChange={() => setPaymentMethod(m.k)} className="mr-2" />
                                    <span className="font-medium">{m.l}</span>
                                    <div className="text-xs text-muted-foreground mt-1">{m.d}</div>
                                </label>
                            ))}
                        </div>
                    </div>

                    <label className="flex items-center gap-2 text-sm card-soft p-4">
                        <input type="checkbox" checked={wantsInstall} onChange={(e) => setWantsInstall(e.target.checked)} data-testid="ck-install" />
                        <span>Doresc servicii de instalare profesională (vom reveni pentru programare)</span>
                    </label>
                </div>

                <div className="card-soft p-6 h-fit sticky top-24">
                    <h3 className="font-heading text-xl mb-4">Sumar comandă</h3>
                    <div className="space-y-2 text-sm mb-4 max-h-64 overflow-y-auto">
                        {cart.items.map((it, i) => (
                            <div key={i} className="flex justify-between gap-2">
                                <span className="truncate">{it.name} × {it.quantity}</span>
                                <span className="font-medium">{fmt(it.unit_price * it.quantity)}</span>
                            </div>
                        ))}
                    </div>
                    <div className="border-t border-border pt-3 space-y-2 text-sm">
                        <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{fmt(subtotal)}</span></div>
                        {userDisc > 0 && <div className="flex justify-between text-primary"><span>Discount cont</span><span>-{fmt(userDisc)}</span></div>}
                        {codeDisc > 0 && <div className="flex justify-between text-primary"><span>Cod {codeInfo?.code}</span><span>-{fmt(codeDisc)}</span></div>}
                        <div className="flex justify-between"><span className="text-muted-foreground">Livrare</span><span>{fmt(shippingCost)}</span></div>
                    </div>
                    <div className="flex items-center gap-2 mt-4">
                        <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Cod reducere" className="input-soft flex-1 !py-2 text-sm" data-testid="ck-code-input" />
                        <button type="button" onClick={applyCode} className="btn-outline !py-2" data-testid="ck-code-apply">Aplică</button>
                    </div>
                    {codeInfo && <div className="mt-2 text-xs text-primary">✓ {codeInfo.code}</div>}
                    {!isGuest && creditBalance > 0 && (
                        <label className="flex items-center justify-between gap-2 mt-3 p-3 rounded-xl bg-primary/5 border border-primary/20 cursor-pointer">
                            <div>
                                <div className="text-sm font-medium">Folosește credite</div>
                                <div className="text-xs text-muted-foreground">{fmt(creditBalance)} disponibili</div>
                            </div>
                            <input type="checkbox" checked={useCredits} onChange={(e) => setUseCredits(e.target.checked)} className="h-4 w-4 accent-primary" />
                        </label>
                    )}
                    {creditDisc > 0 && (
                        <div className="flex justify-between text-primary text-sm mt-2">
                            <span>Credite aplicate</span><span>-{fmt(creditDisc)}</span>
                        </div>
                    )}
                    {!isGuest && loyaltyStatus && loyaltyStatus.points >= 100 && (
                        <label className="flex items-center justify-between gap-2 mt-3 p-3 rounded-xl bg-amber-50 border border-amber-200 cursor-pointer">
                            <div>
                                <div className="text-sm font-medium">Folosește puncte loialitate</div>
                                <div className="text-xs text-muted-foreground">{loyaltyStatus.points} puncte = {fmt(loyaltyRedeemValue)} reducere</div>
                            </div>
                            <input type="checkbox" checked={useLoyaltyPoints} onChange={(e) => setUseLoyaltyPoints(e.target.checked)} className="h-4 w-4 accent-amber-500" />
                        </label>
                    )}
                    {loyaltyDisc > 0 && (
                        <div className="flex justify-between text-amber-600 text-sm mt-2">
                            <span>Puncte loialitate ({loyaltyPointsToRedeem} pct.)</span><span>-{fmt(loyaltyDisc)}</span>
                        </div>
                    )}
                    <div className="border-t border-border mt-4 pt-4 flex justify-between font-heading text-xl">
                        <span>Total</span><span className="text-primary">{fmt(finalTotal)}</span>
                    </div>
                    <button type="submit" disabled={loading} className="btn-primary w-full mt-6" data-testid="checkout-pay-btn">
                        {loading ? "Se procesează..." : paymentMethod === "stripe" ? "Plătește cu cardul" : "Plasează comanda"}
                    </button>
                    <div className="mt-3 text-center text-[11px] text-muted-foreground">{paymentMethod === "stripe" ? "Plată securizată Stripe" : "Fără plată online acum"}</div>
                </div>
            </div>
        </form>
    );
}
